// Package imports:
import React, { useEffect, useMemo, useState } from 'react';
import cx from 'classnames';
// Component imports:
import Button from '../../../ui-elements/Button/Button';
import Loading from '../../../ui-elements/Loading/Loading';
import ErrorAlert from '../../ErrorAlert/ErrorAlert';
import ExampleReportButton from '../../ExampleReportButton/ExampleReportButton';
// Service imports:
import { GET_KELDAN_API_URL } from '../../../services/config';
import { useBuyingProcess } from '../../../services/buyhook';
import { ErrorMessages } from '../../../services/errorMessages';
// Type imports:
import { Fetched } from '../../../types/Types';
import { ICompanyViewModel, CompanyAssetsReport } from '../../../types/CompanyTypes';
import { IKeldanApiResponse } from '../../../types/KeldanTypes';

interface IOwnProps {
    large?: boolean,
    assetType: 'properties' | 'vehicles' | 'constructions'
}
const CompanyAssetsTab: React.FC<ICompanyViewModel & IOwnProps> = ({
    id,
    isAuthenticated,
    large,
    assetType
}) => {
    // Current report.
    const [report, setReport] = useState<Fetched<CompanyAssetsReport>>(null);
    // Use different variable based on Asset Type (properties, vehicles or constructions).
    const {
        fetchDataUrl,
        buyReportUrl,
        viewReportUrl,
        assetName,
        dataSourceText
    } = useMemo(() => {
        const viewReportUrl = (report === null || report instanceof Error)
            ? undefined
            : (assetType === 'properties')
            ? `/Fyrirtaeki/Fasteignir-Skoda/${id}?eventId=${report.eventId}`
            : (assetType === 'vehicles')
            ? `/Fyrirtaeki/Okutaeki-Skoda/${id}?eventId=${report.eventId}`
            : `/Fyrirtaeki/Vinnuvelar-Skoda/${id}?eventId=${report.eventId}`
        // Properties
        if (assetType === 'properties') {
            return {
                fetchDataUrl: `${GET_KELDAN_API_URL()}/Company/GetCompanyPropertiesReportInfo/${id}`,
                buyReportUrl: `${GET_KELDAN_API_URL()}/Company/BuyPropertiesReport/?id=${id}`,
                viewReportUrl,
                assetName: 'Fasteignir',
                dataSourceText: 'fasteignaskrá'
            }
        }
        // Vehicles
        else if (assetType === 'vehicles') {
            return {
                fetchDataUrl: `${GET_KELDAN_API_URL()}/Company/GetCompanyVehiclesReportInfo/${id}`,
                buyReportUrl: `${GET_KELDAN_API_URL()}/Company/BuyVehiclesReport/?id=${id}`,
                viewReportUrl,
                assetName: 'Ökutæki',
                dataSourceText: 'ökutækjaskrá'
            }
        }
        // Constructions
        else {
            return {
                fetchDataUrl: `${GET_KELDAN_API_URL()}/Company/GetCompanyConstructionsReportInfo/${id}`,
                buyReportUrl: `${GET_KELDAN_API_URL()}/Company/BuyConstructionsReport/?id=${id}`,
                viewReportUrl,
                assetName: 'Vinnuvélar',
                dataSourceText: 'vinnuvélaskrá'
            }
        }
    }, [ id, assetType, report ]);

    // Get current report.
    const fetchData = async () => {
        try {
            const headers = {
                'Content-Type': 'application/json; charset=utf-8'
            };
            const response = await fetch(fetchDataUrl, {
                method: 'GET',
                headers
            });
            if (response.ok) {
                if (response.redirected) {
                    window.location.href = response.url;
                } else {
                    const body = await response.json();
                    if (body.message !== "Ekkert fannst") {
                        setReport(body);
                    } else {
                        setReport(new Error('Ekkert fannst'));
                    }
                }
            } else {
                setReport(new Error(ErrorMessages.RequestFailed));
            }
        } catch (e) {
            setReport(new Error(ErrorMessages.OtherShort));
        }
    }
    useEffect(() => {
        if (isAuthenticated)
            fetchData();
    }, [isAuthenticated]);

    // Buying process hook
    const { tryPurchaseItem, isItemLoading, resetState, ModalsAndToasts } = useBuyingProcess(async (option: boolean, modalReference: string) => {
        try {
            const headers = {
                'Content-Type': 'application/json; charset=utf-8'
            };
            const response = await fetch(buyReportUrl+`&reference=${modalReference}`, {
                method: 'GET',
                headers
            });
            if (response.ok) {
                if (response.redirected) {
                    window.location.href = response.url;
                    return;
                }
                const responseBody: IKeldanApiResponse = await response.json();
                if (responseBody.message) {
                    const { message } = responseBody;
                    if (message) {
                        if (message === ErrorMessages.NoCard) {
                            resetState('no card')
                        } else {
                            resetState({
                                type: 'alert',
                                headText: message
                            })
                        }

                        if (message === ErrorMessages.NoProperties || message === ErrorMessages.NoVehicles || message === ErrorMessages.NoConstructions) {
                            fetchData();
                        }
                    } else {
                        resetState('bad request');
                    }
                }
            } else {
                resetState('response not ok');
            }
        } catch (e) {
            resetState('network error');
        }
    }, dataSourceText);

    return (
        <div className='KCL_company-assets-tab'>
            <div className={cx("buy-current-row", {'large': large})}>
                <span>Gildandi skráning</span>
                {(isAuthenticated)
                    ? (
                        <Button
                            showLoader={isItemLoading(true)}
                            size='sm'
                            buttonType='buy'
                            onClick={() => tryPurchaseItem(true, 'confirm-modal-with-reference')}
                        >
                            Kaupa
                        </Button>
                    ) : (
                        <Button
                            size='sm'
                            buttonType='buy'
                            anchorProps={{href: `/Innskraning?ReturnUrl=${window.location.pathname}`}}
                        >
                            Kaupa
                        </Button>
                    )
                }
            </div>
            {(!isAuthenticated)
            ? null
            : (report === null)
            ? <Loading className='Loading' />
            : (report instanceof Error)
            ? (
                (report.message === 'Ekkert fannst')
                ? null
                : <ErrorAlert error={report} />
            ) : (
                <div className="owned-report-values">
                    <div className="owned-report-head-row">
                        <span>Skýrsla keypt: {report.reportBought}</span>
                        {report.owned || (report.sold && report.sold !== -1)
                            ? <Button
                                buttonType='secondary'
                                anchorProps={{
                                    href: viewReportUrl
                                }}
                                size='sm'
                            >
                                Skoða
                            </Button>
                            : null
                        }
                    </div>
                    <div className="owned-row">{assetName} í eigu: {report.owned}</div>
                    {report.sold !== -1 ? <div className="sold-row">{assetName} seldar: {report.sold}</div> : null}
                </div>
            )}
            <div className='example-row'>
                <ExampleReportButton
                    size='sm'
                    reportType={assetType === 'properties'
                        ? 'assets'
                        : assetType === 'vehicles'
                        ? 'vehicles-owned'
                        : 'constructions-owned'
                    }
                />
            </div>
            <ModalsAndToasts />
        </div>
    );
}

export default CompanyAssetsTab;